import { ApolloClient, NormalizedCacheObject } from '@apollo/client'
import { IncomingMessage, ServerResponse } from 'http'
import type { SSRConfig } from 'next-i18next'
import { ParsedUrlQuery } from 'querystring'
import { handleTokenParamOnSSR } from '@/auth/utils'
import { paths } from '@/constants/paths'
import { PayItForwardOfferDataProvider } from '@/contexts/PayItForwardOffersDataContext'
import { Seo } from '@/layout/Seo'
import { ContentOnlyLayout } from '@/layouts/ContentOnlyLayout'
import { NextPageWithLayout } from '@/layouts/types'
import { MarketingVideoPopup } from '@/organisms/MarketingVideoPopup'
import { Captcha } from '@/services/Captcha'
import { getOfferById, getOffersForProject, Offer } from '@/services/PifService'
import { getTheatricalRelease } from '@/services/Theatrical'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import DatadogRumFull from '@/utils/datadog/DatadogRumFull'
import { ExpressCheckoutComplete } from './ExpressCheckoutComplete'
import { ExpressCheckoutView } from './components/ExpressCheckoutView'

interface ServerParams {
  params: ParsedUrlQuery
  query: ParsedUrlQuery
  locale: string
  federationClient: ApolloClient<NormalizedCacheObject>
  req: IncomingMessage & { cookies: Partial<{ [p: string]: string }> }
  res: ServerResponse
  next404: () => { notFound: true }
  translations: SSRConfig
}

export const getExpressCheckoutPageProps = async ({
  params,
  federationClient,
  locale,
  next404,
  region,
  translations,
}: ServerParams & { region?: string }) => {
  const projectSlug = getItemOrFirstEntry(params.projectSlug)
  if (!projectSlug) return next404()

  const [theatricalRelease, offers] = await Promise.all([
    getTheatricalRelease({ theatricalSlug: projectSlug, client: federationClient }),
    getOffersForProject({ projectSlug: projectSlug }, federationClient),
  ])
  if (!theatricalRelease || !offers.length) return next404()

  return {
    props: {
      theatricalRegion: region?.toLowerCase() ?? 'us',
      locale,
      offers,
      projectSlug,
      projectName: theatricalRelease.title ?? '',
      ...translations,
    },
  }
}

export const getExpressCheckoutCompletePageProps = async ({
  params,
  query,
  locale,
  federationClient,
  req,
  res,
  next404,
  translations,
}: ServerParams) => {
  const projectSlug = getItemOrFirstEntry(params?.projectSlug)
  const offerId = getItemOrFirstEntry(params?.offerId)

  if (!projectSlug || !offerId) {
    return next404()
  }

  // If a token is present in the query, establish session + cookie and redirect without the token
  const redirect = await handleTokenParamOnSSR(req, res, query)
  if (redirect) {
    return { redirect }
  }

  const [theatricalRelease, offer] = await Promise.all([
    getTheatricalRelease({ theatricalSlug: projectSlug, client: federationClient }),
    getOfferById({ projectSlug, offerId }, federationClient),
  ])

  if (!theatricalRelease || !offer) {
    return next404()
  }

  return {
    props: {
      locale,
      offer,
      projectSlug,
      projectName: theatricalRelease.title ?? '',
      ...translations,
    },
  }
}

export interface ExpressCheckoutPageProps {
  offers: Array<Offer>
  locale: string
  projectSlug: string
  projectName: string
}

export const ExpressCheckoutPage: NextPageWithLayout<ExpressCheckoutPageProps> = ({
  offers,
  projectSlug,
  locale,
  projectName,
}) => {
  const url = `${paths.getBaseURL()}/express-checkout/${projectSlug}`
  const title = 'Pay it Forward Express Checkout | Angel'
  const description = `Gift tickets for others to view ${projectName} for free.`
  const openGraph = {
    description,
    title,
    url,
  }

  return (
    <>
      <Captcha />
      <DatadogRumFull />
      <Seo canonical={url} description={description} noindex nofollow openGraph={openGraph} title={title} />
      <MarketingVideoPopup className={'z-[999]'} />
      <PayItForwardOfferDataProvider offers={offers}>
        <ExpressCheckoutView locale={locale} projectSlug={projectSlug} projectName={projectName} />
      </PayItForwardOfferDataProvider>
    </>
  )
}

ExpressCheckoutPage.getLayout = (page) => {
  return <ContentOnlyLayout isDarkMode={false}>{page}</ContentOnlyLayout>
}

export interface ExpressCheckoutCompleteProps {
  offer: Offer
  projectSlug: string
  projectName: string
}

export const ExpressCheckoutCompletePage: NextPageWithLayout<ExpressCheckoutCompleteProps> = ({
  offer,
  projectSlug,
  projectName,
}) => {
  const url = `${paths.getBaseURL()}/express-checkout/${projectSlug}`
  const title = 'Pay it Forward Express Checkout | Angel'
  const description = `Gift tickets for others to view ${projectName} for free.`
  const openGraph = {
    description,
    title,
    url,
  }

  return (
    <>
      <Captcha />
      <DatadogRumFull />
      <Seo canonical={url} description={description} noindex nofollow openGraph={openGraph} title={title} />
      <ExpressCheckoutComplete projectName={projectName} projectSlug={projectSlug} offer={offer} isDarkMode={false} />
    </>
  )
}

ExpressCheckoutCompletePage.getLayout = (page) => {
  return <ContentOnlyLayout isDarkMode={false}>{page}</ContentOnlyLayout>
}
